/**
 * Generieke zoom/pan-wrapper voor de niet-React-Flow render-paden (Roadmap,
 * Roadmap-matrix, Heat-map, Gap/Plateau-diff, Glossary). Bootst de zoom-UX van
 * de React-Flow-canvas na zodat élk diagramtype dezelfde besturing heeft:
 *
 *   - **muiswiel / pinch** → zoom in/uit naar de cursor;
 *   - **middelste of rechter muisknop slepen** → pannen (net als RF
 *     `panOnDrag=[1,2]`); het browser-context-menu wordt op dit vlak ALTIJD
 *     onderdrukt, net als op het React-Flow-canvas — op macOS vuurt
 *     `contextmenu` namelijk al bij muis-omlaag, dus een menu dat pas ná een
 *     sleep werd tegengehouden verscheen altijd en brak het pannen af;
 *   - **spatie + linksslepen** → pannen (RF's `panActivationKeyCode`);
 *   - **dubbele rechtsklik** → uitzoomen, zoals op het React-Flow-canvas;
 *   - **`+`/`=`** zoom in, **`-`** uit, **`0`** terug naar 100% — zonder
 *     modifier (anders triggert Chrome's page-zoom) en alleen wanneer de muis
 *     boven dit canvas is én de focus niet in een invoerveld staat.
 *
 * Implementatie via CSS `transform: translate() scale()` op een binnen-laag;
 * de buiten-laag clipt (`overflow:hidden`). Target-gebaseerde drag-drop (bv. de
 * matrix-linkerkolom) blijft werken omdat die op `event.target`/`dataTransfer`
 * leunt, niet op coördinaten.
 */
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const MIN_ZOOM = 0.1;
const MAX_ZOOM = 2;
const WHEEL_FACTOR = 1.1;

function inEditableField(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (target.isContentEditable) return true;
  if (target.closest(".cm-editor")) return true;
  return false;
}

const clampZoom = (z: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));

interface ZoomPanProps {
  children: ReactNode;
  /** Extra classes op de buiten-laag (bv. `flex-1`). */
  className?: string;
  /**
   * Breedte van de transform-laag. `"max"` (default) = `max-content` zodat
   * brede lane-layouts horizontaal overlopen en je kunt pannen (Roadmap,
   * matrix). `"full"` = `100%` zodat responsive tabellen/grids hun breedte
   * behouden (Glossary, diff-views); daar pan/zoom je vooral verticaal.
   */
  contentWidth?: "max" | "full";
  /**
   * Stabiele sleutel (bv. diagram-id) waaronder het zoom/pan-niveau bewaard
   * blijft. De canvas remount bij elke diagram-wissel; via deze module-cache
   * onthoudt elk diagram z'n eigen zoom-niveau over remounts heen.
   */
  persistKey?: string;
  /**
   * De zoom-opdrachten naar buiten, zodat een canvas-menu ze kan aanroepen.
   * Wordt één keer aangeroepen zodra ze klaarstaan.
   */
  onActies?: (acties: ZoomPanActies) => void;
  /**
   * Rechtsklik op het vlak. Het browsermenu is dan al tegengehouden; wie deze
   * meegeeft toont er z'n eigen menu voor, zoals het canvas-menu op het
   * banen-canvas.
   */
  onContextMenu?: (e: { clientX: number; clientY: number }) => void;
}

// Module-cache: diagram-id → laatst-bekende view. Overleeft component-remounts
// (diagram-wissel) zodat een diagram z'n zoom-niveau terugkrijgt.
const VIEW_CACHE = new Map<string, { scale: number; x: number; y: number }>();

/** Wat een canvas-menu met de weergave kan doen. */
export interface ZoomPanActies {
  inzoomen: () => void;
  uitzoomen: () => void;
  terug: () => void;
  passend: () => void;
}

export function ZoomPan({
  children,
  className,
  contentWidth = "max",
  persistKey,
  onActies,
  onContextMenu: opContextMenu,
}: ZoomPanProps) {
  const { t } = useTranslation();
  const outerRef = useRef<HTMLDivElement | null>(null);
  const [view, setView] = useState(
    () => (persistKey && VIEW_CACHE.get(persistKey)) || { scale: 1, x: 0, y: 0 },
  );
  const viewRef = useRef(view);
  viewRef.current = view;
  if (persistKey) VIEW_CACHE.set(persistKey, view);
  const hovered = useRef(false);

  // Zoom rond een punt (px, py) relatief aan de buiten-laag zodat dat punt
  // visueel op z'n plek blijft.
  const zoomAround = useCallback((nextScaleRaw: number, px: number, py: number) => {
    setView((cur) => {
      const nextScale = clampZoom(nextScaleRaw);
      if (nextScale === cur.scale) return cur;
      const worldX = (px - cur.x) / cur.scale;
      const worldY = (py - cur.y) / cur.scale;
      return {
        scale: nextScale,
        x: px - worldX * nextScale,
        y: py - worldY * nextScale,
      };
    });
  }, []);

  const zoomToCenter = useCallback(
    (factor: number) => {
      const el = outerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      zoomAround(viewRef.current.scale * factor, rect.width / 2, rect.height / 2);
    },
    [zoomAround],
  );

  const reset = useCallback(() => setView({ scale: 1, x: 0, y: 0 }), []);
  /** Voor de dubbele rechtsklik — zie `onContextMenu` hieronder. */
  const laatsteRechts = useRef(0);
  /** Was dit de twééde van een dubbele rechtsklik? Dan geen menu. */
  const dubbel = useRef(false);

  /**
   * Alles in beeld: de schaal waarbij de hele tekening past, linksboven
   * uitgelijnd. Het React-Flow-canvas heeft `fitView`; zonder tegenhanger
   * hier zou hetzelfde menu op het ene canvas iets kunnen wat het op het
   * andere niet kan.
   */
  const passend = useCallback(() => {
    const buiten = outerRef.current;
    const binnen = buiten?.firstElementChild as HTMLElement | null;
    if (!buiten || !binnen) return;
    // `scrollWidth`/`scrollHeight` zijn de CSS-maten van de inhoud; een
    // transform verandert die niet. Delen door de huidige schaal is dus fout
    // en leverde bij 100% al meteen de kleinste zoomstand op.
    const breed = binnen.scrollWidth;
    const hoog = binnen.scrollHeight;
    if (breed <= 0 || hoog <= 0) return;
    // Een tabblad dat niet vooraan staat blijft gemount met maat nul. Daar
    // "passend maken" levert de kleinste zoomstand op, en die staat er dan
    // zodra je terugklikt.
    if (buiten.clientWidth <= 0 || buiten.clientHeight <= 0) return;
    const marge = 16;
    const schaal = clampZoom(
      Math.min(
        (buiten.clientWidth - marge * 2) / breed,
        (buiten.clientHeight - marge * 2) / hoog,
        1,
      ),
    );
    setView({ scale: schaal, x: marge, y: marge });
  }, []);

  useEffect(() => {
    onActies?.({
      inzoomen: () => zoomToCenter(WHEEL_FACTOR),
      uitzoomen: () => zoomToCenter(1 / WHEEL_FACTOR),
      terug: reset,
      passend,
    });
  }, [onActies, zoomToCenter, reset, passend]);

  // Muiswiel = zoom (non-passive zodat preventDefault de page-scroll stopt).
  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const factor = e.deltaY < 0 ? WHEEL_FACTOR : 1 / WHEEL_FACTOR;
      zoomAround(viewRef.current.scale * factor, e.clientX - rect.left, e.clientY - rect.top);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [zoomAround]);

  // Toetsenbord +/-/0 — gespiegeld op de RF-canvas-handler (geen modifier,
  // editable-guard). Alleen actief wanneer de muis boven dit canvas is.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!hovered.current) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (inEditableField(document.activeElement)) return;
      if (e.key === "+" || e.key === "=") {
        e.preventDefault();
        zoomToCenter(WHEEL_FACTOR);
      } else if (e.key === "-") {
        e.preventDefault();
        zoomToCenter(1 / WHEEL_FACTOR);
      } else if (e.key === "0") {
        e.preventDefault();
        reset();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [zoomToCenter, reset]);

  // Pan met middelste (button 1) of rechter (button 2) muisknop — of met
  // links zolang de spatiebalk ingedrukt is, zoals React Flow.
  const panning = useRef<{ startX: number; startY: number; ox: number; oy: number } | null>(null);
  const didPan = useRef(false);
  const [spatie, setSpatie] = useState(false);
  const spatieRef = useRef(false);
  spatieRef.current = spatie;

  useEffect(() => {
    const omlaag = (e: KeyboardEvent) => {
      if (e.code !== "Space" || !hovered.current) return;
      if (inEditableField(document.activeElement)) return;
      // Anders scrollt de pagina onder het canvas weg.
      e.preventDefault();
      setSpatie(true);
    };
    const omhoog = (e: KeyboardEvent) => {
      if (e.code !== "Space") return;
      setSpatie(false);
    };
    // De toets kan losgelaten worden terwijl het venster geen focus heeft;
    // dan blijft 'ie anders "ingedrukt" staan.
    const kwijt = () => setSpatie(false);
    window.addEventListener("keydown", omlaag);
    window.addEventListener("keyup", omhoog);
    window.addEventListener("blur", kwijt);
    return () => {
      window.removeEventListener("keydown", omlaag);
      window.removeEventListener("keyup", omhoog);
      window.removeEventListener("blur", kwijt);
    };
  }, []);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    const metSpatie = e.button === 0 && spatieRef.current;
    if (e.button !== 1 && e.button !== 2 && !metSpatie) return;
    e.preventDefault();
    didPan.current = false;
    panning.current = {
      startX: e.clientX,
      startY: e.clientY,
      ox: viewRef.current.x,
      oy: viewRef.current.y,
    };
  }, []);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const p = panning.current;
      if (!p) return;
      const dx = e.clientX - p.startX;
      const dy = e.clientY - p.startY;
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) didPan.current = true;
      setView((cur) => ({ ...cur, x: p.ox + dx, y: p.oy + dy }));
    };
    const onUp = (e: MouseEvent) => {
      const bezig = panning.current !== null;
      panning.current = null;
      // Rechtsklik zonder sleep = het menu. Mét sleep was het pannen, en dan
      // hoort er niets te verschijnen — dat is precies het verschil dat je op
      // een muis voelt en waar een menu bij het indrukken overheen walst.
      if (!bezig || e.button !== 2) return;
      if (didPan.current || dubbel.current) {
        didPan.current = false;
        return;
      }
      opContextMenu?.(e);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [opContextMenu]);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: pan/zoom-oppervlak, geen semantische control; muis-drag stuurt de viewport, geen toetsbesturing
    <div
      ref={outerRef}
      className={`relative overflow-hidden ${spatie ? "cursor-grab" : ""} ${className ?? ""}`}
      role="presentation"
      onMouseEnter={() => {
        hovered.current = true;
      }}
      onMouseLeave={() => {
        hovered.current = false;
        panning.current = null;
      }}
      onMouseDown={onMouseDown}
      onContextMenu={(e) => {
        // ALTIJD tegenhouden, niet alleen na een sleep. Op macOS vuurt
        // `contextmenu` bij muis-OMLAAG, dus bij een rechter-drag verscheen
        // het browsermenu vóór de eerste beweging en was het pannen voorbij
        // voor het begon. Zo doet het React-Flow-canvas het ook
        // (`onCanvasContextMenu`), inclusief de dubbele rechtsklik die
        // uitzoomt.
        //
        // Het eigen menu komt NIET hier maar bij loslaten: anders verschijnt
        // 'ie zodra je de rechterknop indrukt om te pannen.
        //
        // Alleen wie een eigen menu meegeeft krijgt deze behandeling. De
        // glossary en de verschil-views tonen tabellen met tekst; daar is het
        // browsermenu juist nuttig (Kopiëren, Zoeken met…) en zou dit een
        // stille verslechtering zijn. Die houden het oude gedrag: alleen ná
        // een sleep tegenhouden.
        if (!opContextMenu) {
          if (didPan.current) {
            e.preventDefault();
            didPan.current = false;
          }
          return;
        }
        e.preventDefault();
        const nu = Date.now();
        if (nu - laatsteRechts.current < 400) {
          laatsteRechts.current = 0;
          dubbel.current = true;
          zoomToCenter(1 / WHEEL_FACTOR);
        } else {
          laatsteRechts.current = nu;
          dubbel.current = false;
        }
      }}
    >
      <div
        style={{
          transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
          transformOrigin: "0 0",
          width: contentWidth === "full" ? "100%" : "max-content",
          height: "max-content",
        }}
      >
        {children}
      </div>

      {/* Zoom-controls, zelfde plek/stijl als de RF-Controls (linksonder). */}
      <div className="absolute bottom-3 left-3 z-10 flex flex-col overflow-hidden rounded border border-slate-300 bg-white shadow-sm">
        <button
          type="button"
          aria-label={t("zoomPan.zoomIn")}
          title={t("zoomPan.zoomIn")}
          className="px-2 py-1 text-slate-600 text-sm hover:bg-slate-100"
          onClick={() => zoomToCenter(WHEEL_FACTOR)}
        >
          +
        </button>
        <button
          type="button"
          aria-label={t("zoomPan.zoomOut")}
          title={t("zoomPan.zoomOut")}
          className="border-slate-200 border-t px-2 py-1 text-slate-600 text-sm hover:bg-slate-100"
          onClick={() => zoomToCenter(1 / WHEEL_FACTOR)}
        >
          −
        </button>
        <button
          type="button"
          aria-label={t("zoomPan.reset")}
          title={t("zoomPan.reset")}
          className="border-slate-200 border-t px-2 py-1 text-[10px] text-slate-600 hover:bg-slate-100"
          onClick={reset}
        >
          {Math.round(view.scale * 100)}%
        </button>
      </div>
    </div>
  );
}
